import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { GoogleMapsProvider } from './contexts/GoogleMapsContext';
import PrivateRoute from './components/PrivateRoute';
import ResellerRoute from './components/ResellerRoute';
import ErrorBoundary from './components/ErrorBoundary';
import './App.css';
import { JoeLargeAvatar } from './components/JoeLargeAvatar';
import CookieConsentBanner from './components/CookieConsentBanner';
import { BrowserPhoneProvider } from './contexts/BrowserPhoneContext';
// Critical path pages (loaded immediately for fast initial render)
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';

// Joe should only appear on authenticated routes. Hiding him on public
// auth/legal pages prevents him from overlapping form fields like the
// SaaS-agreement checkbox at the bottom of the signup page on iPad.
const JOE_HIDDEN_PATH_PREFIXES = [
  '/login',
  '/signup',
  '/verify-email-required',
  '/forgot-password',
  '/reset-password',
  '/accept-invite',
  '/terms',
  '/privacy',
  '/sms-terms',
  '/cookies',
  '/acceptable-use',
  '/token-disclaimer',
  // Customer-facing public links (from SMS/email) — customers must NEVER see Joe.
  '/estimate/confirm',
  '/job/confirm',
  '/meeting/rsvp',
  '/estimate/sign',
  '/invoice/pay',
  '/share',
  '/route/shared',
  '/change-order/approve',
];

const GatedJoeLargeAvatar: React.FC = () => {
  const location = useLocation();
  const path = location.pathname || '/';
  const hide = JOE_HIDDEN_PATH_PREFIXES.some(prefix => path === prefix || path.startsWith(prefix + '/'));
  if (hide) return null;
  return <JoeLargeAvatar initialPosition="bottom-right" />;
};

// Loading fallback component for lazy-loaded routes
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
);

// Lazy-loaded pages (code-split for smaller initial bundle)
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const ResetPasswordEntry = lazy(() => import('./pages/ResetPasswordEntry'));
const AcceptInvitePage = lazy(() => import('./pages/AcceptInvitePage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const SMSTermsPage = lazy(() => import('./pages/SMSTermsPage'));
const CookiePolicyPage = lazy(() => import('./pages/CookiePolicyPage'));
const AcceptableUsePage = lazy(() => import('./pages/AcceptableUsePage'));
const TokenDisclaimerPage = lazy(() => import('./pages/TokenDisclaimerPage'));
const SmartContractDisclosurePage = lazy(() => import('./pages/SmartContractDisclosurePage'));
const ZoomDocsPage = lazy(() => import('./pages/ZoomDocsPage'));
const ZoomTestPlanPage = lazy(() => import('./pages/ZoomTestPlanPage'));
const Dashboard = lazy(() => import('./pages/DashboardCustomizable'));
const CustomerList = lazy(() => import('./pages/CustomerList'));
const CustomerDetail = lazy(() => import('./pages/CustomerDetail'));
const MyTasks = lazy(() => import('./pages/MyTasks'));
const TaskLog = lazy(() => import('./pages/TaskLog'));
const AIAgentSetupWizard = lazy(() => import('./pages/AIAgentSetupWizard'));
const ResellerCustomers = lazy(() => import('./pages/ResellerCustomers'));
const ResellerSignup = lazy(() => import('./pages/ResellerSignup'));
const ResellerApprovals = lazy(() => import('./pages/ResellerApprovals'));
const SuperAdminResellerTasks = lazy(() => import('./pages/SuperAdminResellerTasks'));
const ResellerSellers = lazy(() => import('./pages/ResellerSellers'));
const ResellerRevenue = lazy(() => import('./pages/ResellerRevenue'));
const ResellerBranding = lazy(() => import('./pages/ResellerBranding'));
const AdminAiUsage = lazy(() => import('./pages/AdminAiUsage'));
const SocialApiKeysPage = lazy(() => import('./pages/SocialApiKeysPage'));
const WorkerEstimate = lazy(() => import('./pages/WorkerEstimate'));
const EstimateSigningPage = lazy(() => import('./pages/EstimateSigningPage'));
const EstimateConfirmPage = lazy(() => import('./pages/EstimateConfirmPage'));
const MeetingRsvpPage = lazy(() => import('./pages/MeetingRsvpPage'));
const InvoicePaymentPage = lazy(() => import('./pages/InvoicePaymentPage'));
const EstimateList = lazy(() => import('./pages/EstimateList'));
const EstimateDetail = lazy(() => import('./pages/EstimateDetail'));
const EstimateCreate = lazy(() => import('./pages/EstimateCreate'));
const PendingBidsPage = lazy(() => import('./pages/PendingBidsPage'));
const ProjectManagement = lazy(() => import('./pages/ProjectManagement'));
const ProjectManagementBoard = lazy(() => import('./pages/ProjectManagementBoard'));
const JobList = lazy(() => import('./pages/JobList'));
const JobDetail = lazy(() => import('./pages/JobDetail'));
const JobForm = lazy(() => import('./pages/JobForm'));
const CalendarPage = lazy(() => import('./pages/CalendarPage'));
const AppointmentDetail = lazy(() => import('./pages/AppointmentDetail'));
const AppointmentList = lazy(() => import('./pages/AppointmentList'));
const RoutePlannerPage = lazy(() => import('./pages/RoutePlannerPage'));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage'));
const MarketingAnalyticsPage = lazy(() => import('./pages/MarketingAnalyticsPage'));
const PaymentsPage = lazy(() => import('./pages/PaymentsPage'));
const ProcessPaymentPage = lazy(() => import('./pages/ProcessPaymentPage'));
const AdminSettingsPage = lazy(() => import('./pages/AdminSettingsPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const MessagingPage = lazy(() => import('./pages/MessagingPage'));
const SubscriptionPage = lazy(() => import('./pages/SubscriptionPage'));
const AddOnsPage = lazy(() => import('./pages/AddOnsPage'));
const WebsiteBuilder = lazy(() => import('./pages/WebsiteBuilder'));
const ImportCustomersPage = lazy(() => import('./pages/ImportCustomersPage'));
const ImportJobsPage = lazy(() => import('./pages/ImportJobsPage'));
const AutomationSettingsPage = lazy(() => import('./pages/AutomationSettingsPage'));
const SavedLineItemsPage = lazy(() => import('./pages/SavedLineItemsPage'));
const AuditLogPage = lazy(() => import('./pages/AuditLogPage'));
const ImportInvoicesPage = lazy(() => import('./pages/ImportInvoicesPage'));
const ImportEstimatesPage = lazy(() => import('./pages/ImportEstimatesPage'));
const ImportPaymentsPage = lazy(() => import('./pages/ImportPaymentsPage'));
const ImportExpensesPage = lazy(() => import('./pages/ImportExpensesPage'));
const ImportBillsPage = lazy(() => import('./pages/ImportBillsPage'));
const ImportSuppliersPage = lazy(() => import('./pages/ImportSuppliersPage'));
const LeadList = lazy(() => import('./pages/LeadList'));
const LeadDetail = lazy(() => import('./pages/LeadDetail'));
const LeadForm = lazy(() => import('./pages/LeadForm'));
const LeadDiscoveryPage = lazy(() => import('./pages/LeadDiscoveryPage'));
const EmailInboxPage = lazy(() => import('./pages/EmailInboxPage'));
const AIAgentsPage = lazy(() => import('./pages/AIAgentsPage'));
const FollowUpListPage = lazy(() => import('./pages/FollowUpListPage'));
const SharedDocumentPage = lazy(() => import('./pages/SharedDocumentPage'));
const SharedRoutePage = lazy(() => import('./pages/SharedRoutePage'));
const ChangeOrderApprovalPage = lazy(() => import('./pages/ChangeOrderApprovalPage'));
const DocumentLibrary = lazy(() => import('./pages/DocumentLibrary'));
const ProjectTimelineList = lazy(() => import('./pages/ProjectTimelineList'));
const ProjectTimelineDetail = lazy(() => import('./pages/ProjectTimelineDetail'));
const ProjectTimelineForm = lazy(() => import('./pages/ProjectTimelineForm'));
const EmployeeManagement = lazy(() => import('./pages/EmployeeManagement'));
const PermissionsManagement = lazy(() => import('./pages/PermissionsManagement'));
const TimeClock = lazy(() => import('./pages/TimeClock'));
const Timesheets = lazy(() => import('./pages/Timesheets'));
const FormBuilder = lazy(() => import('./pages/FormBuilderEnhanced'));
const AddTokenPage = lazy(() => import('./pages/AddTokenPage'));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));
const EstimatesCalendarPage = lazy(() => import('./pages/EstimatesCalendarPage'));
const JobsCalendarPage = lazy(() => import('./pages/JobsCalendarPage'));
const EmployeeCalendarPage = lazy(() => import('./pages/EmployeeCalendarPage'));
const CustomerPortalLogin = lazy(() => import('./pages/CustomerPortalLogin'));
const CustomerPortalDashboard = lazy(() => import('./pages/CustomerPortalDashboard'));
const CustomerPortalJobsList = lazy(() => import('./pages/CustomerPortalJobsList'));
const CustomerPortalJobDetail = lazy(() => import('./pages/CustomerPortalJobDetail'));
const CustomerPortalEstimatesList = lazy(() => import('./pages/CustomerPortalEstimatesList'));
const CustomerPortalEstimateDetail = lazy(() => import('./pages/CustomerPortalEstimateDetail'));
const CustomerPortalPhotoGallery = lazy(() => import('./pages/CustomerPortalPhotoGallery'));
const CustomerPortalProfile = lazy(() => import('./pages/CustomerPortalProfile'));
const CustomerPortalHelp = lazy(() => import('./pages/CustomerPortalHelp'));
const HelpCenter = lazy(() => import('./pages/HelpCenter'));
const SimpleBooks = lazy(() => import('./pages/SimpleBooks'));
const CustomerMessages = lazy(() => import('./pages/CustomerMessages'));
const CustomerPortalNotifications = lazy(() => import('./pages/CustomerPortalNotifications'));
const CustomerPortalJobReview = lazy(() => import('./pages/CustomerPortalJobReview'));
const CustomerPortalSelections = lazy(() => import('./pages/CustomerPortalSelections'));
const CustomerPortalAllPhotos = lazy(() => import('./pages/CustomerPortalAllPhotos'));
const CustomerPortalPayments = lazy(() => import('./pages/CustomerPortalPayments'));
const CustomerPortalCheckout = lazy(() => import('./pages/CustomerPortalCheckout'));
const ReviewRequestManagement = lazy(() => import('./pages/ReviewRequestManagement'));
const BlogAdmin = lazy(() => import('./pages/BlogAdmin'));
const ReferralProgramManagement = lazy(() => import('./pages/ReferralProgramManagement'));
const SendReviewRequest = lazy(() => import('./pages/SendReviewRequest'));
const SendReferralInvite = lazy(() => import('./pages/SendReferralInvite'));
const AdminContractorsPage = lazy(() => import('./pages/AdminContractorsPage'));
const SuperAdminDashboard = lazy(() => import('./pages/superadmin/Dashboard'));
const BrowserPhoneCosts = lazy(() => import('./pages/superadmin/BrowserPhoneCosts'));
const UsersList = lazy(() => import('./pages/superadmin/UsersList'));
const UserDetail = lazy(() => import('./pages/superadmin/UserDetail'));
const CompaniesList = lazy(() => import('./pages/superadmin/CompaniesList'));
const CompanyDetail = lazy(() => import('./pages/superadmin/CompanyDetail'));
const InventoryList = lazy(() => import('./pages/InventoryList'));
const InventoryDetail = lazy(() => import('./pages/InventoryDetail'));
const InventoryForm = lazy(() => import('./components/InventoryForm'));
const SupplierList = lazy(() => import('./pages/SupplierList'));
const SupplierForm = lazy(() => import('./components/SupplierForm'));
const PurchaseOrderList = lazy(() => import('./pages/PurchaseOrderList'));
const PurchaseOrderForm = lazy(() => import('./pages/PurchaseOrderForm'));
const BulkInventoryImport = lazy(() => import('./pages/BulkInventoryImport'));
const NotificationCenter = lazy(() => import('./pages/NotificationCenter'));
const CatalogPage = lazy(() => import('./pages/CatalogPage'));
const MaterialOrdersPage = lazy(() => import('./pages/MaterialOrdersPage'));
const PermitsPage = lazy(() => import('./pages/PermitsPage'));
const PermitForm = lazy(() => import('./pages/PermitForm'));
const UtilityLocatesPage = lazy(() => import('./pages/UtilityLocatesPage'));
const ParcelLookupPage = lazy(() => import('./pages/ParcelLookupPage'));
const ExpensesPage = lazy(() => import('./pages/ExpensesPage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const BalanceSheetPage = lazy(() => import('./pages/BalanceSheetPage'));
const SalesTaxReportPage = lazy(() => import('./pages/SalesTaxReportPage'));
const ChangeOrders = lazy(() => import('./pages/ChangeOrders'));
const ChangeOrderDetail = lazy(() => import('./pages/ChangeOrderDetail'));
const ChangeOrderForm = lazy(() => import('./pages/ChangeOrderForm'));
const RFIs = lazy(() => import('./pages/RFIs'));
const RFIDetail = lazy(() => import('./pages/RFIDetail'));
const RFIForm = lazy(() => import('./pages/RFIForm'));
const Submittals = lazy(() => import('./pages/Submittals'));
const SubmittalDetail = lazy(() => import('./pages/SubmittalDetail'));
const SubmittalForm = lazy(() => import('./pages/SubmittalForm'));
const DailyLogs = lazy(() => import('./pages/DailyLogs'));
const DailyLogDetail = lazy(() => import('./pages/DailyLogDetail'));
const DailyLogForm = lazy(() => import('./pages/DailyLogForm'));
const AIABillingList = lazy(() => import('./pages/AIABillingList'));
const AIABillingDetail = lazy(() => import('./pages/AIABillingDetail'));
const AIABillingForm = lazy(() => import('./pages/AIABillingForm'));
const PayApplicationDetail = lazy(() => import('./pages/PayApplicationDetail'));
const CashFlowDashboard = lazy(() => import('./pages/CashFlowDashboard'));
const SafetyDashboard = lazy(() => import('./pages/SafetyDashboard'));
const IncidentList = lazy(() => import('./pages/IncidentList'));
const IncidentDetail = lazy(() => import('./pages/IncidentDetail'));
const IncidentForm = lazy(() => import('./pages/IncidentForm'));
const SafetyInspectionList = lazy(() => import('./pages/SafetyInspectionList'));
const SafetyInspectionDetail = lazy(() => import('./pages/SafetyInspectionDetail'));
const SafetyInspectionForm = lazy(() => import('./pages/SafetyInspectionForm'));
const BMPInspectionList = lazy(() => import('./pages/BMPInspectionList'));
const BMPInspectionDetail = lazy(() => import('./pages/BMPInspectionDetail'));
const BMPInspectionForm = lazy(() => import('./pages/BMPInspectionForm'));
const EquipmentList = lazy(() => import('./pages/EquipmentList'));
const EquipmentDetail = lazy(() => import('./pages/EquipmentDetail'));
const EquipmentForm = lazy(() => import('./pages/EquipmentForm'));
const EquipmentMap = lazy(() => import('./pages/EquipmentMap'));
const EquipmentLocationHistory = lazy(() => import('./pages/EquipmentLocationHistory'));
const MeetingList = lazy(() => import('./pages/MeetingList'));
const MeetingDetail = lazy(() => import('./pages/MeetingDetail'));
const MeetingForm = lazy(() => import('./pages/MeetingForm'));
const WarrantyList = lazy(() => import('./pages/WarrantyList'));
const WarrantyDetail = lazy(() => import('./pages/WarrantyDetail'));
const WarrantyForm = lazy(() => import('./pages/WarrantyForm'));
const WarrantyClaimDetail = lazy(() => import('./pages/WarrantyClaimDetail'));
const WarrantyTypes = lazy(() => import('./pages/WarrantyTypes'));
const ObservationList = lazy(() => import('./pages/ObservationList'));
const ObservationDetail = lazy(() => import('./pages/ObservationDetail'));
const ObservationForm = lazy(() => import('./pages/ObservationForm'));
const LienWaiverList = lazy(() => import('./pages/LienWaiverList'));
const LienWaiverDetail = lazy(() => import('./pages/LienWaiverDetail'));
const LienWaiverForm = lazy(() => import('./pages/LienWaiverForm'));
const BidList = lazy(() => import('./pages/BidList'));
const BidDetail = lazy(() => import('./pages/BidDetail'));
const BidForm = lazy(() => import('./pages/BidForm'));
const SelectionList = lazy(() => import('./pages/SelectionList'));
const SelectionDetail = lazy(() => import('./pages/SelectionDetail'));
const SelectionForm = lazy(() => import('./pages/SelectionForm'));
const QADashboard = lazy(() => import('./pages/QADashboard'));
const ChecklistList = lazy(() => import('./pages/ChecklistList'));
const ChecklistDetail = lazy(() => import('./pages/ChecklistDetail'));
const ChecklistBuilder = lazy(() => import('./pages/ChecklistBuilder'));
const InspectionList = lazy(() => import('./pages/InspectionList'));
const InspectionDetail = lazy(() => import('./pages/InspectionDetail'));
const InspectionForm = lazy(() => import('./pages/InspectionForm'));
const DeficiencyList = lazy(() => import('./pages/DeficiencyList'));
const DeficiencyDetail = lazy(() => import('./pages/DeficiencyDetail'));
const DeficiencyForm = lazy(() => import('./pages/DeficiencyForm'));
const BidLevelingList = lazy(() => import('./pages/BidLevelingList'));
const BidLevelingDetail = lazy(() => import('./pages/BidLevelingDetail'));
const BidLevelingForm = lazy(() => import('./pages/BidLevelingForm'));
const PrequalificationList = lazy(() => import('./pages/PrequalificationList'));
const PrequalificationDetail = lazy(() => import('./pages/PrequalificationDetail'));
const PrequalificationForm = lazy(() => import('./pages/PrequalificationForm'));
const Blueprints = lazy(() => import('./pages/Blueprints'));
const ProjectsDashboard = lazy(() => import('./pages/ProjectsDashboard'));
const SocialMediaPage = lazy(() => import('./pages/SocialMediaPage'));
const PhoneAssistant = lazy(() => import('./pages/PhoneAssistant'));
const VoiceRoi = lazy(() => import('./pages/VoiceRoi'));

function App() {
  return (
    <GoogleMapsProvider>
      <AuthProvider>
        {/* React Router 7: v7_startTransition and v7_relativeSplatPath are the
            defaults now, so the v6 `future` opt-in prop no longer exists. */}
        <Router>
        <BrowserPhoneProvider>
        <div className="App">
          <ErrorBoundary>
          <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/reseller-signup" element={<ResellerSignup />} />
            {/* First-run setup for the AI phone agent. Redirects to /ai-agents
                on its own if an assistant already exists, so it can be linked
                from anywhere without a second existence check. */}
            <Route path="/ai-agents/setup" element={
              <PrivateRoute>
                <AIAgentSetupWizard />
              </PrivateRoute>
            } />
            <Route path="/verify-email-required" element={<VerifyEmailPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordEntry />} />
            <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
            <Route path="/accept-invite/:token" element={<AcceptInvitePage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/sms-terms" element={<SMSTermsPage />} />
            <Route path="/cookies" element={<CookiePolicyPage />} />
            <Route path="/acceptable-use" element={<AcceptableUsePage />} />
            <Route path="/token-disclaimer" element={<TokenDisclaimerPage />} />
            <Route path="/smart-contract-disclosure" element={<SmartContractDisclosurePage />} />
            <Route path="/docs/zoom" element={<ZoomDocsPage />} />
            <Route path="/docs/zoom-test-plan" element={<ZoomTestPlanPage />} />
            <Route path="/estimate/sign/:token" element={<EstimateSigningPage />} />
            <Route path="/estimate/confirm/:token" element={<EstimateConfirmPage />} />
            <Route path="/job/confirm/:token" element={<EstimateConfirmPage />} />
            <Route path="/meeting/rsvp/:token" element={<MeetingRsvpPage />} />
            <Route path="/estimate/:estimateId/sign" element={<EstimateSigningPage />} />
            <Route path="/invoice/pay/:token" element={<InvoicePaymentPage />} />
            <Route path="/estimate/:estimateId" element={<WorkerEstimate />} />
            <Route path="/share/:token" element={<SharedDocumentPage />} />
            <Route path="/route/shared/:shareToken" element={<SharedRoutePage />} />
            <Route path="/change-order/approve/:token" element={<ChangeOrderApprovalPage />} />

            {/* Customer Portal routes */}
            <Route path="/customer-portal/login" element={<CustomerPortalLogin />} />
            <Route path="/customer-portal/dashboard" element={<CustomerPortalDashboard />} />
            <Route path="/customer-portal" element={<Navigate to="/customer-portal/login" replace />} />
            <Route path="/customer-portal/jobs" element={<CustomerPortalJobsList />} />
            <Route path="/customer-portal/jobs/:id" element={<CustomerPortalJobDetail />} />
            <Route path="/customer-portal/estimates" element={<CustomerPortalEstimatesList />} />
            <Route path="/customer-portal/estimates/:id" element={<CustomerPortalEstimateDetail />} />
            <Route path="/customer-portal/photos" element={<CustomerPortalAllPhotos />} />
            <Route path="/customer-portal/photos/:jobId" element={<CustomerPortalPhotoGallery />} />
            <Route path="/customer-portal/profile" element={<CustomerPortalProfile />} />
            <Route path="/customer-portal/help" element={<CustomerPortalHelp />} />
            <Route path="/customer-portal/messages" element={<CustomerMessages />} />
            <Route path="/customer-portal/notifications" element={<CustomerPortalNotifications />} />
            <Route path="/customer-portal/selections" element={<CustomerPortalSelections />} />
            <Route path="/customer-portal/payments" element={<CustomerPortalPayments />} />
            <Route path="/customer-portal/checkout" element={<CustomerPortalCheckout />} />
            <Route path="/customer-portal/jobs/:jobId/review" element={<CustomerPortalJobReview />} />

            {/* Onboarding */}
            <Route path="/onboarding" element={
              <PrivateRoute>
                <OnboardingPage />
              </PrivateRoute>
            } />

            {/* Protected routes */}
            <Route path="/dashboard" element={
              <PrivateRoute>
                <Dashboard />
              </PrivateRoute>
            } />
            <Route path="/help" element={
              <PrivateRoute>
                <HelpCenter />
              </PrivateRoute>
            } />

            <Route path="/customers" element={
              <PrivateRoute>
                <CustomerList />
              </PrivateRoute>
            } />
            <Route path="/reseller/customers" element={
              <PrivateRoute>
                <ResellerRoute>
                  <ResellerCustomers />
                </ResellerRoute>
              </PrivateRoute>
            } />
            <Route path="/reseller/sellers" element={
              <PrivateRoute>
                <ResellerRoute>
                  <ResellerSellers />
                </ResellerRoute>
              </PrivateRoute>
            } />
            <Route path="/reseller/revenue" element={
              <PrivateRoute>
                <ResellerRoute>
                  <ResellerRevenue />
                </ResellerRoute>
              </PrivateRoute>
            } />
            <Route path="/reseller/branding" element={
              <PrivateRoute>
                <ResellerRoute>
                  <ResellerBranding />
                </ResellerRoute>
              </PrivateRoute>
            } />
            <Route path="/customers/new" element={
              <PrivateRoute>
                <CustomerList />
              </PrivateRoute>
            } />

            <Route path="/customers/:id" element={
              <PrivateRoute>
                <CustomerDetail />
              </PrivateRoute>
            } />

            <Route path="/tasks" element={
              <PrivateRoute>
                <MyTasks />
              </PrivateRoute>
            } />

            <Route path="/task-log" element={
              <PrivateRoute>
                <TaskLog />
              </PrivateRoute>
            } />

            <Route path="/customers/send-review" element={
              <PrivateRoute>
                <SendReviewRequest />
              </PrivateRoute>
            } />

            <Route path="/customers/send-referral" element={
              <PrivateRoute>
                <SendReferralInvite />
              </PrivateRoute>
            } />

            {/* Projects Dashboard - Unified hub for all project operations */}
            <Route path="/projects" element={
              <PrivateRoute>
                <ProjectsDashboard />
              </PrivateRoute>
            } />
            <Route path="/projects/*" element={
              <PrivateRoute>
                <ProjectsDashboard />
              </PrivateRoute>
            } />

            <Route path="/project-board" element={
              <PrivateRoute>
                <ProjectManagementBoard />
              </PrivateRoute>
            } />

            <Route path="/project-management" element={
              <PrivateRoute>
                <ProjectManagement />
              </PrivateRoute>
            } />

            <Route path="/jobs" element={
              <PrivateRoute>
                <JobList />
              </PrivateRoute>
            } />

            <Route path="/jobs/create" element={
              <PrivateRoute>
                <JobForm />
              </PrivateRoute>
            } />

            <Route path="/jobs/edit/:id" element={
              <PrivateRoute>
                <JobForm />
              </PrivateRoute>
            } />

            <Route path="/jobs/:id" element={
              <PrivateRoute>
                <JobDetail />
              </PrivateRoute>
            } />

            <Route path="/calendar" element={
              <PrivateRoute>
                <CalendarPage />
              </PrivateRoute>
            } />

            <Route path="/calendar/estimates" element={
              <PrivateRoute>
                <EstimatesCalendarPage />
              </PrivateRoute>
            } />

            <Route path="/calendar/jobs" element={
              <PrivateRoute>
                <JobsCalendarPage />
              </PrivateRoute>
            } />

            <Route path="/calendar/employees" element={
              <PrivateRoute>
                <EmployeeCalendarPage />
              </PrivateRoute>
            } />

            <Route path="/appointments" element={
              <PrivateRoute>
                <AppointmentList />
              </PrivateRoute>
            } />

            <Route path="/appointments/:id" element={
              <PrivateRoute>
                <AppointmentDetail />
              </PrivateRoute>
            } />

            <Route path="/route-planner" element={
              <PrivateRoute>
                <RoutePlannerPage />
              </PrivateRoute>
            } />

            <Route path="/estimates" element={
              <PrivateRoute>
                <EstimateList />
              </PrivateRoute>
            } />

            <Route path="/estimates/create" element={
              <PrivateRoute>
                <EstimateCreate />
              </PrivateRoute>
            } />

            <Route path="/estimates/edit/:id" element={
              <PrivateRoute>
                <EstimateCreate />
              </PrivateRoute>
            } />

            <Route path="/estimates/:id" element={
              <PrivateRoute>
                <EstimateDetail />
              </PrivateRoute>
            } />

            <Route path="/pending-bids" element={
              <PrivateRoute>
                <PendingBidsPage />
              </PrivateRoute>
            } />

            <Route path="/analytics" element={
              <PrivateRoute>
                <AnalyticsPage />
              </PrivateRoute>
            } />

            <Route path="/marketing-analytics" element={
              <PrivateRoute>
                <MarketingAnalyticsPage />
              </PrivateRoute>
            } />

            <Route path="/simplebooks" element={
              <PrivateRoute>
                <SimpleBooks />
              </PrivateRoute>
            } />

            <Route path="/simplebooks/*" element={
              <PrivateRoute>
                <SimpleBooks />
              </PrivateRoute>
            } />

            <Route path="/social-media" element={
              <PrivateRoute>
                <SocialMediaPage />
              </PrivateRoute>
            } />

            <Route path="/ai-agents" element={
              <PrivateRoute>
                <AIAgentsPage />
              </PrivateRoute>
            } />

            <Route path="/payments" element={
              <PrivateRoute>
                <PaymentsPage />
              </PrivateRoute>
            } />

            <Route path="/process-payment" element={
              <PrivateRoute>
                <ProcessPaymentPage />
              </PrivateRoute>
            } />

            <Route path="/leads" element={
              <PrivateRoute>
                <LeadList />
              </PrivateRoute>
            } />

            <Route path="/leads/new" element={
              <PrivateRoute>
                <LeadForm />
              </PrivateRoute>
            } />

            <Route path="/leads/:id" element={
              <PrivateRoute>
                <LeadDetail />
              </PrivateRoute>
            } />

            <Route path="/leads/discover" element={
              <PrivateRoute>
                <LeadDiscoveryPage />
              </PrivateRoute>
            } />

            <Route path="/email" element={
              <PrivateRoute>
                <EmailInboxPage />
              </PrivateRoute>
            } />

            <Route path="/follow-ups" element={
              <PrivateRoute>
                <FollowUpListPage />
              </PrivateRoute>
            } />

            <Route path="/documents" element={
              <PrivateRoute>
                <DocumentLibrary />
              </PrivateRoute>
            } />

            <Route path="/blueprints" element={
              <PrivateRoute>
                <Blueprints />
              </PrivateRoute>
            } />

            <Route path="/timelines" element={
              <PrivateRoute>
                <ProjectTimelineList />
              </PrivateRoute>
            } />

            <Route path="/timelines/new" element={
              <PrivateRoute>
                <ProjectTimelineForm />
              </PrivateRoute>
            } />

            <Route path="/timelines/:id" element={
              <PrivateRoute>
                <ProjectTimelineDetail />
              </PrivateRoute>
            } />

            <Route path="/timelines/edit/:id" element={
              <PrivateRoute>
                <ProjectTimelineForm />
              </PrivateRoute>
            } />

            <Route path="/employees" element={
              <PrivateRoute>
                <EmployeeManagement />
              </PrivateRoute>
            } />

            <Route path="/time-clock" element={
              <PrivateRoute>
                <TimeClock />
              </PrivateRoute>
            } />

            <Route path="/timesheets" element={
              <PrivateRoute>
                <Timesheets />
              </PrivateRoute>
            } />

            <Route path="/permissions" element={
              <PrivateRoute>
                <PermissionsManagement />
              </PrivateRoute>
            } />

            <Route path="/form-builder" element={
              <PrivateRoute>
                <FormBuilder />
              </PrivateRoute>
            } />

            {/* Settings page for GPS/Timeclock and other settings */}
            <Route path="/settings" element={
              <PrivateRoute>
                <SettingsPage />
              </PrivateRoute>
            } />
            <Route path="/profile" element={<Navigate to="/admin?tab=profile" replace />} />

            <Route path="/subscription" element={
              <PrivateRoute>
                <SubscriptionPage />
              </PrivateRoute>
            } />

            <Route path="/addons" element={
              <PrivateRoute>
                <AddOnsPage />
              </PrivateRoute>
            } />

            <Route path="/website-builder" element={
              <PrivateRoute>
                <WebsiteBuilder />
              </PrivateRoute>
            } />

            <Route path="/phone-assistant" element={
              <PrivateRoute>
                <PhoneAssistant />
              </PrivateRoute>
            } />
            <Route path="/voice/roi" element={
              <PrivateRoute>
                <VoiceRoi />
              </PrivateRoute>
            } />

            <Route path="/messages" element={
              <PrivateRoute>
                <MessagingPage />
              </PrivateRoute>
            } />

            <Route path="/admin" element={
              <PrivateRoute>
                <AdminSettingsPage />
              </PrivateRoute>
            } />
            <Route path="/admin/ai-usage" element={
              <PrivateRoute>
                <AdminAiUsage />
              </PrivateRoute>
            } />
            <Route path="/admin/social-api" element={
              <PrivateRoute>
                <SocialApiKeysPage />
              </PrivateRoute>
            } />

            <Route path="/admin/review-requests" element={
              <PrivateRoute>
                <ReviewRequestManagement />
              </PrivateRoute>
            } />

            <Route path="/admin/blog" element={
              <PrivateRoute>
                <BlogAdmin />
              </PrivateRoute>
            } />

            <Route path="/admin/referrals" element={
              <PrivateRoute>
                <ReferralProgramManagement />
              </PrivateRoute>
            } />

            <Route path="/admin/contractors" element={
              <PrivateRoute>
                <AdminContractorsPage />
              </PrivateRoute>
            } />

            <Route path="/admin/automation-settings" element={
              <PrivateRoute>
                <AutomationSettingsPage />
              </PrivateRoute>
            } />

            <Route path="/admin/line-items" element={
              <PrivateRoute>
                <SavedLineItemsPage />
              </PrivateRoute>
            } />

            <Route path="/admin/audit-log" element={
              <PrivateRoute>
                <AuditLogPage />
              </PrivateRoute>
            } />

            <Route path="/superadmin" element={
              <PrivateRoute>
                <SuperAdminDashboard />
              </PrivateRoute>
            } />

            <Route path="/superadmin/phone-costs" element={
              <PrivateRoute>
                <BrowserPhoneCosts />
              </PrivateRoute>
            } />

            <Route path="/superadmin/resellers" element={
              <PrivateRoute>
                <ResellerApprovals />
              </PrivateRoute>
            } />

            <Route path="/superadmin/reseller-tasks" element={
              <PrivateRoute>
                <SuperAdminResellerTasks />
              </PrivateRoute>
            } />

            <Route path="/superadmin/users" element={
              <PrivateRoute>
                <UsersList />
              </PrivateRoute>
            } />

            <Route path="/superadmin/users/:id" element={
              <PrivateRoute>
                <UserDetail />
              </PrivateRoute>
            } />

            <Route path="/superadmin/companies" element={
              <PrivateRoute>
                <CompaniesList />
              </PrivateRoute>
            } />

            <Route path="/superadmin/companies/:id" element={
              <PrivateRoute>
                <CompanyDetail />
              </PrivateRoute>
            } />

            <Route path="/add-token" element={
              <PrivateRoute>
                <AddTokenPage />
              </PrivateRoute>
            } />

            <Route path="/import/customers" element={
              <PrivateRoute>
                <ImportCustomersPage />
              </PrivateRoute>
            } />

            <Route path="/import/jobs" element={
              <PrivateRoute>
                <ImportJobsPage />
              </PrivateRoute>
            } />

            <Route path="/import/invoices" element={
              <PrivateRoute>
                <ImportInvoicesPage />
              </PrivateRoute>
            } />

            <Route path="/import/estimates" element={
              <PrivateRoute>
                <ImportEstimatesPage />
              </PrivateRoute>
            } />

            <Route path="/import/payments" element={
              <PrivateRoute>
                <ImportPaymentsPage />
              </PrivateRoute>
            } />

            <Route path="/import/inventory" element={
              <PrivateRoute>
                <BulkInventoryImport />
              </PrivateRoute>
            } />

            <Route path="/import/expenses" element={
              <PrivateRoute>
                <ImportExpensesPage />
              </PrivateRoute>
            } />

            <Route path="/import/bills" element={
              <PrivateRoute>
                <ImportBillsPage />
              </PrivateRoute>
            } />

            <Route path="/import/suppliers" element={
              <PrivateRoute>
                <ImportSuppliersPage />
              </PrivateRoute>
            } />

            {/* Inventory Management routes */}
            <Route path="/inventory" element={
              <PrivateRoute>
                <InventoryList />
              </PrivateRoute>
            } />

            <Route path="/inventory/new" element={
              <PrivateRoute>
                <InventoryForm isEdit={false} />
              </PrivateRoute>
            } />

            <Route path="/inventory/:id" element={
              <PrivateRoute>
                <InventoryDetail />
              </PrivateRoute>
            } />

            <Route path="/inventory/:id/edit" element={
              <PrivateRoute>
                <InventoryForm isEdit={true} />
              </PrivateRoute>
            } />

            {/* Supplier Management routes */}
            <Route path="/suppliers" element={
              <PrivateRoute>
                <SupplierList />
              </PrivateRoute>
            } />

            <Route path="/suppliers/new" element={
              <PrivateRoute>
                <SupplierForm isEdit={false} />
              </PrivateRoute>
            } />

            <Route path="/suppliers/:id/edit" element={
              <PrivateRoute>
                <SupplierForm isEdit={true} />
              </PrivateRoute>
            } />

            {/* Purchase Order routes */}
            <Route path="/purchase-orders" element={
              <PrivateRoute>
                <PurchaseOrderList />
              </PrivateRoute>
            } />

            <Route path="/purchase-orders/new" element={
              <PrivateRoute>
                <PurchaseOrderForm isEdit={false} />
              </PrivateRoute>
            } />

            <Route path="/purchase-orders/:id/edit" element={
              <PrivateRoute>
                <PurchaseOrderForm isEdit={true} />
              </PrivateRoute>
            } />

            {/* Notifications */}
            <Route path="/notifications" element={
              <PrivateRoute>
                <NotificationCenter />
              </PrivateRoute>
            } />

            {/* Catalog */}
            <Route path="/catalog" element={
              <PrivateRoute>
                <CatalogPage />
              </PrivateRoute>
            } />

            {/* Material Orders */}
            <Route path="/material-orders" element={
              <PrivateRoute>
                <MaterialOrdersPage />
              </PrivateRoute>
            } />

            {/* Permits */}
            <Route path="/permits" element={
              <PrivateRoute>
                <PermitsPage />
              </PrivateRoute>
            } />
            <Route path="/permits/new" element={
              <PrivateRoute>
                <PermitForm />
              </PrivateRoute>
            } />
            <Route path="/permits/:id/edit" element={
              <PrivateRoute>
                <PermitForm />
              </PrivateRoute>
            } />

            {/* Utility Locates */}
            <Route path="/utility-locates" element={
              <PrivateRoute>
                <UtilityLocatesPage />
              </PrivateRoute>
            } />

            {/* Parcel Lookup */}
            <Route path="/parcel-lookup" element={
              <PrivateRoute>
                <ParcelLookupPage />
              </PrivateRoute>
            } />

            {/* Expenses (Simple Books) */}
            <Route path="/expenses" element={
              <PrivateRoute>
                <ExpensesPage />
              </PrivateRoute>
            } />
            <Route path="/reports" element={
              <PrivateRoute>
                <ReportsPage />
              </PrivateRoute>
            } />
            <Route path="/reports/balance-sheet" element={
              <PrivateRoute>
                <BalanceSheetPage />
              </PrivateRoute>
            } />
            <Route path="/reports/sales-tax" element={
              <PrivateRoute>
                <SalesTaxReportPage />
              </PrivateRoute>
            } />

            {/* Change Orders */}
            <Route path="/change-orders" element={
              <PrivateRoute>
                <ChangeOrders />
              </PrivateRoute>
            } />
            <Route path="/change-orders/new" element={
              <PrivateRoute>
                <ChangeOrderForm />
              </PrivateRoute>
            } />
            <Route path="/change-orders/:id" element={
              <PrivateRoute>
                <ChangeOrderDetail />
              </PrivateRoute>
            } />
            <Route path="/change-orders/:id/edit" element={
              <PrivateRoute>
                <ChangeOrderForm />
              </PrivateRoute>
            } />

            {/* RFIs */}
            <Route path="/rfis" element={
              <PrivateRoute>
                <RFIs />
              </PrivateRoute>
            } />
            <Route path="/rfis/new" element={
              <PrivateRoute>
                <RFIForm />
              </PrivateRoute>
            } />
            <Route path="/rfis/:id" element={
              <PrivateRoute>
                <RFIDetail />
              </PrivateRoute>
            } />
            <Route path="/rfis/:id/edit" element={
              <PrivateRoute>
                <RFIForm />
              </PrivateRoute>
            } />

            {/* Submittals */}
            <Route path="/submittals" element={
              <PrivateRoute>
                <Submittals />
              </PrivateRoute>
            } />
            <Route path="/submittals/new" element={
              <PrivateRoute>
                <SubmittalForm />
              </PrivateRoute>
            } />
            <Route path="/submittals/:id" element={
              <PrivateRoute>
                <SubmittalDetail />
              </PrivateRoute>
            } />
            <Route path="/submittals/:id/edit" element={
              <PrivateRoute>
                <SubmittalForm />
              </PrivateRoute>
            } />

            {/* Daily Logs */}
            <Route path="/daily-logs" element={
              <PrivateRoute>
                <DailyLogs />
              </PrivateRoute>
            } />
            <Route path="/daily-logs/new" element={
              <PrivateRoute>
                <DailyLogForm />
              </PrivateRoute>
            } />
            <Route path="/daily-logs/:id" element={
              <PrivateRoute>
                <DailyLogDetail />
              </PrivateRoute>
            } />
            <Route path="/daily-logs/:id/edit" element={
              <PrivateRoute>
                <DailyLogForm />
              </PrivateRoute>
            } />

            {/* AIA Billing */}
            <Route path="/aia-billing" element={
              <PrivateRoute>
                <AIABillingList />
              </PrivateRoute>
            } />
            <Route path="/aia-billing/new" element={
              <PrivateRoute>
                <AIABillingForm />
              </PrivateRoute>
            } />
            <Route path="/aia-billing/:id" element={
              <PrivateRoute>
                <AIABillingDetail />
              </PrivateRoute>
            } />
            <Route path="/aia-billing/:id/edit" element={
              <PrivateRoute>
                <AIABillingForm />
              </PrivateRoute>
            } />
            <Route path="/aia-billing/application/:id" element={
              <PrivateRoute>
                <PayApplicationDetail />
              </PrivateRoute>
            } />

            {/* Cash Flow Dashboard */}
            <Route path="/cash-flow" element={
              <PrivateRoute>
                <CashFlowDashboard />
              </PrivateRoute>
            } />

            {/* Safety & Incidents */}
            <Route path="/safety" element={
              <PrivateRoute>
                <SafetyDashboard />
              </PrivateRoute>
            } />
            <Route path="/safety/incidents" element={
              <PrivateRoute>
                <IncidentList />
              </PrivateRoute>
            } />
            <Route path="/safety/incidents/new" element={
              <PrivateRoute>
                <IncidentForm />
              </PrivateRoute>
            } />
            <Route path="/safety/incidents/:id" element={
              <PrivateRoute>
                <IncidentDetail />
              </PrivateRoute>
            } />
            <Route path="/safety/incidents/:id/edit" element={
              <PrivateRoute>
                <IncidentForm />
              </PrivateRoute>
            } />
            <Route path="/safety/inspections" element={
              <PrivateRoute>
                <SafetyInspectionList />
              </PrivateRoute>
            } />
            <Route path="/safety/inspections/new" element={
              <PrivateRoute>
                <SafetyInspectionForm />
              </PrivateRoute>
            } />
            <Route path="/safety/inspections/:id" element={
              <PrivateRoute>
                <SafetyInspectionDetail />
              </PrivateRoute>
            } />
            <Route path="/safety/inspections/:id/edit" element={
              <PrivateRoute>
                <SafetyInspectionForm />
              </PrivateRoute>
            } />

            {/* BMP Inspections */}
            <Route path="/bmp-inspections" element={
              <PrivateRoute>
                <BMPInspectionList />
              </PrivateRoute>
            } />
            <Route path="/bmp-inspections/new" element={
              <PrivateRoute>
                <BMPInspectionForm />
              </PrivateRoute>
            } />
            <Route path="/bmp-inspections/:id" element={
              <PrivateRoute>
                <BMPInspectionDetail />
              </PrivateRoute>
            } />
            <Route path="/bmp-inspections/:id/edit" element={
              <PrivateRoute>
                <BMPInspectionForm />
              </PrivateRoute>
            } />

            {/* Safety - Observations */}
            <Route path="/safety/observations" element={
              <PrivateRoute>
                <ObservationList />
              </PrivateRoute>
            } />
            <Route path="/safety/observations/new" element={
              <PrivateRoute>
                <ObservationForm />
              </PrivateRoute>
            } />
            <Route path="/safety/observations/:id" element={
              <PrivateRoute>
                <ObservationDetail />
              </PrivateRoute>
            } />
            <Route path="/safety/observations/:id/edit" element={
              <PrivateRoute>
                <ObservationForm />
              </PrivateRoute>
            } />

            {/* Safety - Meetings/Toolbox Talks */}
            <Route path="/safety/meetings" element={
              <PrivateRoute>
                <MeetingList />
              </PrivateRoute>
            } />
            <Route path="/safety/meetings/new" element={
              <PrivateRoute>
                <MeetingForm />
              </PrivateRoute>
            } />
            <Route path="/safety/meetings/:id" element={
              <PrivateRoute>
                <MeetingDetail />
              </PrivateRoute>
            } />
            <Route path="/safety/meetings/:id/edit" element={
              <PrivateRoute>
                <MeetingForm />
              </PrivateRoute>
            } />

            {/* Safety - Equipment */}
            <Route path="/safety/equipment" element={
              <PrivateRoute>
                <EquipmentList />
              </PrivateRoute>
            } />
            <Route path="/safety/equipment/new" element={
              <PrivateRoute>
                <EquipmentForm />
              </PrivateRoute>
            } />
            <Route path="/safety/equipment/:id" element={
              <PrivateRoute>
                <EquipmentDetail />
              </PrivateRoute>
            } />
            <Route path="/safety/equipment/:id/edit" element={
              <PrivateRoute>
                <EquipmentForm />
              </PrivateRoute>
            } />

            {/* Equipment */}
            <Route path="/equipment" element={
              <PrivateRoute>
                <EquipmentList />
              </PrivateRoute>
            } />
            <Route path="/equipment/new" element={
              <PrivateRoute>
                <EquipmentForm />
              </PrivateRoute>
            } />
            <Route path="/equipment/:id" element={
              <PrivateRoute>
                <EquipmentDetail />
              </PrivateRoute>
            } />
            <Route path="/equipment/:id/edit" element={
              <PrivateRoute>
                <EquipmentForm />
              </PrivateRoute>
            } />
            <Route path="/equipment/map" element={
              <PrivateRoute>
                <EquipmentMap />
              </PrivateRoute>
            } />
            <Route path="/equipment/:id/history" element={
              <PrivateRoute>
                <EquipmentLocationHistory />
              </PrivateRoute>
            } />

            {/* Meetings */}
            <Route path="/meetings" element={
              <PrivateRoute>
                <MeetingList />
              </PrivateRoute>
            } />
            <Route path="/meetings/new" element={
              <PrivateRoute>
                <MeetingForm />
              </PrivateRoute>
            } />
            <Route path="/meetings/:id" element={
              <PrivateRoute>
                <MeetingDetail />
              </PrivateRoute>
            } />
            <Route path="/meetings/:id/edit" element={
              <PrivateRoute>
                <MeetingForm />
              </PrivateRoute>
            } />

            {/* Warranty Routes */}
            <Route path="/warranties" element={
              <PrivateRoute>
                <WarrantyList />
              </PrivateRoute>
            } />
            <Route path="/warranties/new" element={
              <PrivateRoute>
                <WarrantyForm />
              </PrivateRoute>
            } />
            <Route path="/warranties/:id" element={
              <PrivateRoute>
                <WarrantyDetail />
              </PrivateRoute>
            } />
            <Route path="/warranties/:id/edit" element={
              <PrivateRoute>
                <WarrantyForm />
              </PrivateRoute>
            } />
            <Route path="/warranties/claims/:claimId" element={
              <PrivateRoute>
                <WarrantyClaimDetail />
              </PrivateRoute>
            } />
            <Route path="/warranty-types" element={
              <PrivateRoute>
                <WarrantyTypes />
              </PrivateRoute>
            } />

            {/* Observation Routes */}
            <Route path="/observations" element={
              <PrivateRoute>
                <ObservationList />
              </PrivateRoute>
            } />
            <Route path="/observations/new" element={
              <PrivateRoute>
                <ObservationForm />
              </PrivateRoute>
            } />
            <Route path="/observations/:id" element={
              <PrivateRoute>
                <ObservationDetail />
              </PrivateRoute>
            } />
            <Route path="/observations/:id/edit" element={
              <PrivateRoute>
                <ObservationForm />
              </PrivateRoute>
            } />

            {/* Lien Waiver Routes */}
            <Route path="/lien-waivers" element={
              <PrivateRoute>
                <LienWaiverList />
              </PrivateRoute>
            } />
            <Route path="/lien-waivers/new" element={
              <PrivateRoute>
                <LienWaiverForm />
              </PrivateRoute>
            } />
            <Route path="/lien-waivers/:id" element={
              <PrivateRoute>
                <LienWaiverDetail />
              </PrivateRoute>
            } />
            <Route path="/lien-waivers/:id/edit" element={
              <PrivateRoute>
                <LienWaiverForm />
              </PrivateRoute>
            } />

            {/* Bidding Routes */}
            <Route path="/bids" element={
              <PrivateRoute>
                <BidList />
              </PrivateRoute>
            } />
            <Route path="/bids/new" element={
              <PrivateRoute>
                <BidForm />
              </PrivateRoute>
            } />
            <Route path="/bids/:id" element={
              <PrivateRoute>
                <BidDetail />
              </PrivateRoute>
            } />
            <Route path="/bids/:id/edit" element={
              <PrivateRoute>
                <BidForm />
              </PrivateRoute>
            } />

            {/* Client Selections Routes */}
            <Route path="/selections" element={
              <PrivateRoute>
                <SelectionList />
              </PrivateRoute>
            } />
            <Route path="/selections/new" element={
              <PrivateRoute>
                <SelectionForm />
              </PrivateRoute>
            } />
            <Route path="/selections/:id" element={
              <PrivateRoute>
                <SelectionDetail />
              </PrivateRoute>
            } />
            <Route path="/selections/:id/edit" element={
              <PrivateRoute>
                <SelectionForm />
              </PrivateRoute>
            } />

            {/* Bid Leveling Routes */}
            <Route path="/bid-leveling" element={
              <PrivateRoute>
                <BidLevelingList />
              </PrivateRoute>
            } />
            <Route path="/bid-leveling/new" element={
              <PrivateRoute>
                <BidLevelingForm />
              </PrivateRoute>
            } />
            <Route path="/bid-leveling/:id" element={
              <PrivateRoute>
                <BidLevelingDetail />
              </PrivateRoute>
            } />
            <Route path="/bid-leveling/:id/edit" element={
              <PrivateRoute>
                <BidLevelingForm />
              </PrivateRoute>
            } />

            {/* QA/QC Routes */}
            <Route path="/qaqc" element={
              <PrivateRoute>
                <QADashboard />
              </PrivateRoute>
            } />
            <Route path="/qaqc/checklists" element={
              <PrivateRoute>
                <ChecklistList />
              </PrivateRoute>
            } />
            <Route path="/qaqc/checklists/new" element={
              <PrivateRoute>
                <ChecklistBuilder />
              </PrivateRoute>
            } />
            <Route path="/qaqc/checklists/:id" element={
              <PrivateRoute>
                <ChecklistDetail />
              </PrivateRoute>
            } />
            <Route path="/qaqc/checklists/:id/edit" element={
              <PrivateRoute>
                <ChecklistBuilder />
              </PrivateRoute>
            } />
            <Route path="/qaqc/inspections" element={
              <PrivateRoute>
                <InspectionList />
              </PrivateRoute>
            } />
            <Route path="/qaqc/inspections/new" element={
              <PrivateRoute>
                <InspectionForm />
              </PrivateRoute>
            } />
            <Route path="/qaqc/inspections/:id" element={
              <PrivateRoute>
                <InspectionDetail />
              </PrivateRoute>
            } />
            <Route path="/qaqc/inspections/:id/edit" element={
              <PrivateRoute>
                <InspectionForm />
              </PrivateRoute>
            } />
            <Route path="/qaqc/deficiencies" element={
              <PrivateRoute>
                <DeficiencyList />
              </PrivateRoute>
            } />
            <Route path="/qaqc/deficiencies/new" element={
              <PrivateRoute>
                <DeficiencyForm />
              </PrivateRoute>
            } />
            <Route path="/qaqc/deficiencies/:id" element={
              <PrivateRoute>
                <DeficiencyDetail />
              </PrivateRoute>
            } />
            <Route path="/qaqc/deficiencies/:id/edit" element={
              <PrivateRoute>
                <DeficiencyForm />
              </PrivateRoute>
            } />

            {/* Prequalification Routes */}
            <Route path="/prequalification" element={
              <PrivateRoute>
                <PrequalificationList />
              </PrivateRoute>
            } />
            <Route path="/prequalification/new" element={
              <PrivateRoute>
                <PrequalificationForm />
              </PrivateRoute>
            } />
            <Route path="/prequalification/:id" element={
              <PrivateRoute>
                <PrequalificationDetail />
              </PrivateRoute>
            } />
            <Route path="/prequalification/:id/edit" element={
              <PrivateRoute>
                <PrequalificationForm />
              </PrivateRoute>
            } />

            {/* Default redirect */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />

            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          </Suspense>
          </ErrorBoundary>
          {/* GDPR Cookie Consent Banner */}
          <CookieConsentBanner />
          {/* Global Joe assistant (single instance, hidden on public auth/legal pages) */}
          <GatedJoeLargeAvatar />
        </div>
        </BrowserPhoneProvider>
      </Router>
    </AuthProvider>
    </GoogleMapsProvider>
  );
}

export default App;
